import React, { useState } from 'react';
import { Box, Slider, TextField, Typography } from '@mui/material';

import { TextOperationService } from '../../../services';
import {
  DEFAULT_EXPAND_TEXT_STATE,
  buildExpandTextRequest,
  type ExpandTextFormState,
} from './buildRequests';
import { useRunTextOperation } from './useTextOperationMutation';
import { TEXT_OPERATION_CATALOG } from './operationCatalog';
import { PanelScaffold } from './PanelScaffold';
import type { TextPanelProps } from './panelProps';

export const ExpandPanel: React.FC<TextPanelProps> = ({
  ensureSession,
  isCreatingSession = false,
  documentId,
  sectionId,
  onResult,
}) => {
  const [state, setState] = useState<ExpandTextFormState>(DEFAULT_EXPAND_TEXT_STATE);
  const { run, mutation, validationError } = useRunTextOperation(
    TextOperationService.expand,
    ensureSession,
    documentId,
    { onResult },
  );

  const needsDocument = !documentId;
  const handleSubmit = () => run((sid, docId) => buildExpandTextRequest(state, sid, docId, sectionId));

  return (
    <PanelScaffold
      meta={TEXT_OPERATION_CATALOG.Expand}
      needsDocument={needsDocument}
      validationError={validationError}
      mutationError={mutation.isError ? mutation.error : null}
      busy={mutation.isPending}
      starting={isCreatingSession}
      submitDisabled={mutation.isPending || isCreatingSession || needsDocument}
      submitLabel="Expand"
      busyLabel="Expanding…"
      onSubmit={handleSubmit}
    >
      <Box>
        <Typography variant="caption" color="text.secondary">
          Expansion factor · {state.expansionFactor.toFixed(1)}×
        </Typography>
        <Slider
          value={state.expansionFactor}
          onChange={(_, v) =>
            setState(s => ({ ...s, expansionFactor: Array.isArray(v) ? v[0] : v }))
          }
          min={1.2}
          max={5}
          step={0.1}
          size="small"
        />
      </Box>
      <TextField
        label="Direction (optional)"
        value={state.direction}
        onChange={e => setState(s => ({ ...s, direction: e.target.value }))}
        size="small"
        fullWidth
        multiline
        minRows={1}
        maxRows={3}
        placeholder="e.g. add sensory detail to the setting"
      />
    </PanelScaffold>
  );
};
